import { useCallback, useEffect, useRef, useState } from 'react';
import usageService from 'src/services/usageService';
import { currentConnection } from 'src/features/Broadcasts/broadcastApi';

// AUTO-WA-39: Meta's figures are slow to fetch, so the last answer is kept in
// this browser and shown at once; a fresh copy loads quietly behind it. The
// server also serves a cached copy (see MetaUsageService), so repeat visits
// never wait on Meta.
const CACHE_PREFIX = 'autoappy.whatsapp.metaUsage.v2';
const cacheKey = (accountId, days) => `${CACHE_PREFIX}.${accountId}.${days}`;

function readCache(key) {
  try {
    const saved = JSON.parse(localStorage.getItem(key) || 'null');
    return saved && saved.data ? saved : null;
  } catch {
    return null;
  }
}

function writeCache(key, data) {
  try {
    localStorage.setItem(key, JSON.stringify({ data, savedAt: Date.now() }));
  } catch {
    // Storage full or blocked: the page still works without the saved copy.
  }
}

export default function useMetaUsage(days) {
  const connection = currentConnection();
  const workspaceId = connection?.workspaceId;
  const accountId = connection?.whatsappAccountId;
  const ready = Boolean(workspaceId && accountId);
  const key = ready ? cacheKey(accountId, days) : null;

  const [state, setState] = useState(() => {
    const saved = key ? readCache(key) : null;
    return { data: saved?.data || null, savedAt: saved?.savedAt || null, loading: !saved && ready, refreshing: false, error: ready ? '' : 'Connect a WhatsApp account to see what Meta charges.' };
  });
  const request = useRef(0);

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (!ready) return;
    const id = request.current + 1;
    request.current = id;
    const saved = readCache(key);
    setState((current) => ({
      ...current,
      data: saved?.data || (refresh ? current.data : null),
      savedAt: saved?.savedAt || (refresh ? current.savedAt : null),
      loading: !saved && !current.data,
      refreshing: refresh || Boolean(saved),
      error: '',
    }));
    try {
      const response = await usageService.get(workspaceId, accountId, { days, refresh });
      const data = response?.data?.data ?? null;
      if (request.current !== id) return;
      if (data) writeCache(key, data);
      setState({ data, savedAt: Date.now(), loading: false, refreshing: false, error: '' });
    } catch (error) {
      if (request.current !== id) return;
      setState((current) => ({
        ...current,
        loading: false,
        refreshing: false,
        error: error?.response?.data?.message || 'Meta’s figures could not be loaded right now.',
      }));
    }
  }, [ready, key, workspaceId, accountId, days]);

  useEffect(() => { load(); }, [load]);

  return { ...state, ready, refresh: () => load({ refresh: true }) };
}
